import type { ArtifactListingEntry, PluginFolderState } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import { Dialog, DialogContent, DialogTitle } from '@crowdex/ui/components/dialog';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { toast } from '@crowdex/ui/components/toaster';
import { Check, Download, LoaderCircle, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { GrantList } from '@/components/plugins-page/plugin-grants';
import { installPluginFolder } from './artifacts-client';
import { refreshArtifactIndex } from './use-artifact-index';

type Folder = Pick<ArtifactListingEntry, 'conversation' | 'path' | 'title'> & {
  pluginFolder: PluginFolderState;
};

function isFolder(artifact: ArtifactListingEntry): artifact is ArtifactListingEntry & Folder {
  return artifact.pluginFolder !== undefined && artifact.conversation !== null;
}

/**
 * A plugin folder's own action on its card and where it is open: install it (or its update) once
 * the user approved what it may do; installed, it says so.
 */
export function PluginFolderActions({
  artifact,
  size = 'xs',
}: {
  artifact: ArtifactListingEntry;
  size?: 'xs' | 'sm';
}) {
  const [approving, setApproving] = useState(false);
  if (!isFolder(artifact)) return null;
  const { status } = artifact.pluginFolder;
  if (status === 'installed')
    return (
      <span className="flex items-center gap-1 px-2 text-muted-foreground text-xs">
        <Check className="size-3.5" />
        Installed
      </span>
    );
  if (
    status !== 'not-installed' &&
    status !== 'update' &&
    status !== 'elsewhere' &&
    status !== 'unchecked'
  )
    return null;
  return (
    <>
      <Button onClick={() => setApproving(true)} size={size} type="button" variant="secondary">
        {status === 'update' ? <RefreshCw /> : <Download />}
        {status === 'update' ? 'Update' : status === 'unchecked' ? 'Install…' : 'Install'}
      </Button>
      {approving && <InstallDialog artifact={artifact} onClose={() => setApproving(false)} />}
    </>
  );
}

function InstallDialog({ artifact, onClose }: { artifact: Folder; onClose: () => void }) {
  const [error, setError] = useState<string | null>(null);
  // A folder not built yet (in the cloud) is built first: what installing it asks.
  const [state, setState] = useState<PluginFolderState | null>(
    artifact.pluginFolder.status === 'unchecked' ? null : artifact.pluginFolder,
  );
  const [working, setWorking] = useState(state === null);
  useEffect(() => {
    if (artifact.conversation === null || state !== null) return;
    let live = true;
    installPluginFolder(artifact.conversation, artifact.path).then(
      (value) => {
        if (!live) return;
        setState(value);
        setWorking(false);
      },
      (reason: unknown) => {
        if (!live) return;
        setWorking(false);
        setError(reason instanceof Error ? reason.message : String(reason));
      },
    );
    return () => {
      live = false;
    };
  }, [artifact, state]);
  const update = state?.status === 'update';
  const title = state?.title ?? artifact.title;
  const installable =
    state !== null &&
    (state.status === 'not-installed' || state.status === 'update' || state.status === 'elsewhere');
  const install = () => {
    const conversation = artifact.conversation;
    if (conversation === null || state === null) return;
    setWorking(true);
    setError(null);
    installPluginFolder(conversation, artifact.path, state.grants).then(
      async () => {
        await refreshArtifactIndex(conversation);
        // Its tab, tools and skills follow at once.
        window.dispatchEvent(new Event('crowdex-plugins-changed'));
        toast.success(`${title} ${update ? 'updated' : 'installed'}`);
        onClose();
      },
      (reason: unknown) => {
        setWorking(false);
        setError(reason instanceof Error ? reason.message : String(reason));
      },
    );
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !working && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>
          {update ? 'Update' : 'Install'} {title}
          {state?.version === undefined ? '' : ` ${state.version}`}
        </DialogTitle>
        {state === null ? (
          error === null && (
            <p className="flex items-center gap-2 text-muted-foreground text-sm">
              <LoaderCircle className="size-4 animate-spin" />
              Building it in its workspace…
            </p>
          )
        ) : installable ? (
          <>
            <p className="text-muted-foreground text-sm">
              {update
                ? state.added.length > 0
                  ? 'This version also asks to:'
                  : 'This version asks for nothing more.'
                : `From ${artifact.path}. It may:`}
            </p>
            {(!update || state.added.length > 0) && (
              <GrantList
                grants={update ? state.added : state.grants}
                label={update ? 'What the update adds' : 'What it may do'}
              />
            )}
            {state.status === 'elsewhere' && state.message !== undefined && (
              <p className="text-muted-foreground text-xs">
                {state.message} Installing moves it to this folder.
              </p>
            )}
          </>
        ) : (
          <InlineMessage
            title={
              state.status === 'installed'
                ? 'It is installed and up to date.'
                : STATUS[state.status]
            }
            tone={state.status === 'invalid' ? 'error' : 'info'}
          >
            {state.message}
          </InlineMessage>
        )}
        {error !== null && (
          <InlineMessage title="It was not installed." tone="error">
            {error}
          </InlineMessage>
        )}
        <div className="flex justify-end gap-2">
          <Button disabled={working} onClick={onClose} size="sm" type="button" variant="ghost">
            Cancel
          </Button>
          {installable && (
            <Button disabled={working} onClick={install} size="sm" type="button">
              {working && <LoaderCircle className="animate-spin" />}
              {update ? 'Approve update' : 'Install'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STATUS: Record<PluginFolderState['status'], string> = {
  unchecked: 'Built when you install it',
  'not-installed': 'Not installed',
  installed: 'Installed',
  update: 'An update waits for your approval',
  elsewhere: 'Installed from another folder',
  unavailable: 'Cannot be installed from here',
  invalid: 'Does not build',
};

/** What a plugin folder is, brings and may do: its artifact's view. */
export function PluginFolderSummary({
  artifact,
  compact = false,
}: {
  artifact: ArtifactListingEntry;
  compact?: boolean;
}) {
  const state = artifact.pluginFolder;
  if (state === undefined) return <div aria-busy="true" className="size-full" />;
  const brings = state.brings;
  const items =
    brings === undefined
      ? []
      : [
          brings.page && 'A tab',
          brings.widgets > 0 && `${brings.widgets} widget${brings.widgets === 1 ? '' : 's'}`,
          brings.tools && 'Agent tools',
          brings.skills && 'Skills',
          brings.mcp > 0 && `${brings.mcp} MCP server${brings.mcp === 1 ? '' : 's'}`,
          brings.connections > 0 &&
            `${brings.connections} connection${brings.connections === 1 ? '' : 's'}`,
          brings.actions > 0 && `${brings.actions} action${brings.actions === 1 ? '' : 's'}`,
        ].filter((item): item is string => typeof item === 'string');
  return (
    <div className="size-full space-y-3 overflow-auto p-4">
      <div>
        <p className="font-medium text-sm">
          {state.title ?? artifact.title}
          {state.version === undefined ? '' : ` ${state.version}`}
        </p>
        <p className="text-muted-foreground text-xs">
          {[state.name, STATUS[state.status]].filter(Boolean).join(' · ')}
        </p>
      </div>
      {state.description !== undefined && <p className="text-sm">{state.description}</p>}
      {state.status === 'invalid' || state.status === 'unavailable' ? (
        state.message !== undefined && (
          <pre className="whitespace-pre-wrap rounded-md bg-muted p-2 text-xs">{state.message}</pre>
        )
      ) : (
        <>
          {items.length > 0 && (
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs">Brings</p>
              <ul className="flex flex-wrap gap-1">
                {items.map((item) => (
                  <li className="rounded-md bg-muted px-1.5 py-0.5 text-xs" key={item}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!compact && (
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs">May</p>
              <GrantList grants={state.grants} label="What it may do" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
